import React, { memo } from 'react';
import type { RefAttributes } from 'react';
import { css } from '@linaria/core';
import clsx from 'clsx';

import { getCellStyle, getCellClassname, isCellEditable } from './utils';
import type { CellRendererProps } from './types';

const cellSelected = css`
  background-color: var(--selected-color) !important;
`;

const loading = css`
  animation: skeleton-fade-in .3s linear forwards,skeleton-glow 1s linear infinite alternate;
  animation-delay: 0s,.3s;
  height: 5px;
  background-clip: padding-box!important;
  border-color: rgba(206,217,224,.2)!important;
  border-radius: 4px;
  box-shadow: none!important;
  color: transparent!important;
  width: calc(100% - 32px);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  @keyframes skeleton-fade-in {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }

  @keyframes skeleton-glow {
    0% {
      background: rgba(206,217,224,.2);
      border-color: rgba(206,217,224,.2);
    }
    100% {
      background: rgba(92,112,128,.2);
      border-color: rgba(92,112,128,.2);
    }
  }
`

const cellLoading = css`
  /* Hide the text. */
  text-align: initial!important;
  text-indent: -99999px;
`

const cellSelectedClassname = `rdg-cell-selected ${cellSelected}`;
const cellLoadingClassname = `rdg-cell-loading ${cellLoading}`;

const cellDraggedOver = css`
  background-color: var(--selecting-color) !important;
`;

const cellDraggedOverClassname = `rdg-cell-dragged-over ${cellDraggedOver}`;

/*const cellDragHandle = css`
  cursor: move;
  position: absolute;
  right: 0;
  bottom: 0;
  width: 8px;
  height: 8px;
  background-color: var(--selection-color);

  &:hover {
    width: 16px;
    height: 16px;
    border: 2px solid var(--selection-color);
    background-color: var(--background-color);
  }
`;
*/

const dragHandle = css`
  cursor: crosshair;
  position: absolute;
  right: 0;
  bottom: 0;
  width: 6px;
  height: 6px;
  background-color: var(--selection-color);
`
const cellDragHandleClassname = `rdg-cell-drag-handle ${dragHandle}`;

const rowReorderHandle = css`
  cursor: grab;
  position: absolute;
  left: 10px;
  top: 0px;
  width: 13px;
  height: 10px;
  padding: 0px 2px;
  margin: 5px 0;
  border-top: 3px dotted var(--border-color);
  border-bottom: 3px dotted var(--border-color);

  &:active{
    cursor: grabbing;
  }
`
const rowReorderHandleClassname = `rdg-row-reorder-handle ${rowReorderHandle}`;

const rowReordering = css`
  cursor: grabbing !important;
`
const rowReorderingClassname = `rdg-row-reordering ${rowReordering}`

const rowHeader = css`
  background-color: var(--header-background-color);
  font-weight: bold;
  cursor: e-resize;
  text-align: right !important;
  padding-left: 38px;
`
const rowHeaderClassname = `rdg-cell-row-header ${rowHeader}`

function Cell<R, SR>({
  cellMouseDownHandler,
  cellStyles,
  column,
  colSpan,
  dragHandleProps,
  dragPos,
  enableDrag,
  isCellSelected,
  isCellFocused,
  isDraggedOver,
  isReorderingRow,
  isRowSelected,
  onContextMenu,
  onDoubleClick,
  onFocus,
  onKeyDown,
  onMouseDown,
  onMouseEnter,
  onRowClick,
  onRowDoubleClick,
  onRowChange,
  reorderRows,
  row,
  rowIdx,
  selectCell,
  updateDraggedOverRange,
  ...props
}: CellRendererProps<R, SR>) {
  const { cellClass } = column;
  const className = getCellClassname(
    column,
    {
      [cellDraggedOverClassname]: isDraggedOver,
      [cellSelectedClassname]: isCellSelected,
      [cellLoadingClassname]: cellStyles?.loading,
      [rowReorderingClassname]: isReorderingRow,
      [rowHeaderClassname]: column.idx === 0,
    },
    typeof cellClass === 'function' ? cellClass(row) : cellClass,
    cellStyles?.classes
  );

  const reorderHandleClasses = clsx(
    rowReorderHandleClassname,
    {
      [rowReorderingClassname]: isReorderingRow
    }
  );

  function selectCellWrapper(openEditor?: boolean | null) {
    selectCell(row, column, openEditor);
  }

  function handleDragEnter(event: React.MouseEvent<HTMLDivElement>) {
    if (dragPos.current?.column && dragPos.current?.startRowIdx >= 0 && dragPos.current?.startIdx >= 0)
      dragPos.current = { ...dragPos.current, lastRowIdx: rowIdx, lastIdx: column.idx };
    updateDraggedOverRange(rowIdx);
    onMouseEnter?.(event);
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    cellMouseDownHandler(event, rowIdx, column);
    onRowClick?.(row, column);
    onMouseDown?.(event);
  }

  function handleContextMenu(event: React.MouseEvent<HTMLDivElement, MouseEvent>) {
    selectCellWrapper();
    onContextMenu?.(event);
  }

  function handleDoubleClick() {
    selectCellWrapper(true); // VS req
    onRowDoubleClick?.(row, column);
  }

  return (
    <div
      role="gridcell"
      aria-colindex={column.idx + 1} // aria-colindex is 1-based
      aria-selected={isCellFocused}
      aria-colspan={colSpan}
      aria-readonly={!isCellEditable(column, row) || undefined}
      className={className}
      style={{ ...getCellStyle(column, colSpan, cellStyles) }}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onKeyDown={onKeyDown}
      onMouseEnter={handleDragEnter}
      onMouseDown={handleMouseDown}
      {...props}
    >
      {!column.rowGroup && (
        <>
          <column.formatter
            column={column}
            row={row}
            isCellSelected={isCellFocused}
            isRowSelected={isRowSelected}
            onRowChange={onRowChange}
          />
          {/*dragHandle*/}
          {enableDrag && column.idx !== 0 && (
            <div
              id="rdg-drag-handle"
              className={cellDragHandleClassname}
            />
          )}
          {reorderRows && column.idx === 0 && (
            <div
              id="rdg-reorder-handle"
              className={reorderHandleClasses}
            />
          )}
        </>
      )}
      {
        cellStyles?.loading && (
          <div className={loading} />
        )
      }
    </div >
  );
}

export default memo(Cell) as <R, SR>(
  props: CellRendererProps<R, SR> & RefAttributes<HTMLDivElement>
) => JSX.Element;
